import React, { useState } from 'react';
import { useContent } from './ContentContext';

/**
 * Inline-editable text.
 *   <Editable id="home.hero.headline" as="h1" className="...">We build…</Editable>
 * Shows the saved value, falling back to the children (original hardcoded text).
 * When the owner is logged in and edit mode is on, the element is clickable and
 * opens a small editor.
 */
export default function Editable({
  id,
  as: Tag = 'span',
  className = '',
  children,
  multiline = false,
  page,
  label,
}) {
  const { get, save, editMode, isOwner } = useContent();
  const fallback = typeof children === 'string' ? children : '';
  const text = get(id, fallback);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const canEdit = isOwner && editMode;

  const start = (e) => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    setDraft(text);
    setOpen(true);
  };

  const commit = async () => {
    setSaving(true);
    await save(id, draft, { page, label, type: multiline ? 'textarea' : 'text' });
    setSaving(false);
    setOpen(false);
  };

  const editClass = canEdit
    ? 'outline outline-1 outline-dashed outline-[#1FA8F5]/70 outline-offset-2 cursor-pointer hover:bg-[#1FA8F5]/10 rounded-sm transition-colors'
    : '';

  return (
    <>
      <Tag
        className={`${className} ${editClass}`.trim()}
        onClick={start}
        title={canEdit ? 'Click to edit' : undefined}
      >
        {text}
      </Tag>

      {open && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
          onClick={() => !saving && setOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Edit {label || id}
            </div>
            {multiline ? (
              <textarea
                autoFocus
                rows={5}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="w-full rounded-md border border-slate-300 p-3 text-sm text-slate-900 focus:border-[#1FA8F5] focus:outline-none"
              />
            ) : (
              <input
                autoFocus
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="w-full rounded-md border border-slate-300 p-3 text-sm text-slate-900 focus:border-[#1FA8F5] focus:outline-none"
              />
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                disabled={saving}
                className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={commit}
                disabled={saving}
                className="rounded-md bg-[#1FA8F5] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B8ED8] disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
